import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  useDisclosure,
  FormErrorMessage,
  VStack,
  Card,
  CardBody,
  IconButton,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  Icon,
  Select,
  Textarea,
  useColorModeValue,
  Image,
  Checkbox,
  Badge
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { FiPackage, FiRefreshCw, FiPlus } from 'react-icons/fi';
import { FaUpload } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import axiosInstance from '../services/axiosInstance';
import CobraSpinner from '../shared/CobraSpinner';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const AdminPacksManager = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [packs, setPacks] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPacks, setFilteredPacks] = useState([]);
  const toast = useToast();

  const [formData, setFormData] = useState({
    nameAr: '',
    name: '',
    description: '',
    descriptionAr: '',
    price: '',
    originalPrice: '',
    duration: 30,
    niveau: '',
    color: '#F2C919',
    isPopular: false
  });
  const [image, setImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setImage(acceptedFiles[0]);
    },
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [packsRes, niveauxRes] = await Promise.all([
        axiosInstance.get('/packs'),
        axiosInstance.get('/niveaux')
      ]);
      const packsData = packsRes.data.packs || packsRes.data.data || packsRes.data;
      setPacks(Array.isArray(packsData) ? packsData : []);
      
      const niveauxData = niveauxRes.data.data || niveauxRes.data;
      setNiveaux(Array.isArray(niveauxData) ? niveauxData : []);
    } catch (error) {
      toast({ title: 'خطأ في جلب البيانات', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (Array.isArray(packs)) {
      setFilteredPacks(
        packs.filter(p => 
          (p.nameAr && p.nameAr.includes(searchTerm)) || 
          (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    } else {
      setFilteredPacks([]);
    }
  }, [searchTerm, packs]);

  const handleOpenModal = (pack = null) => {
    if (pack) {
      setFormData({
        nameAr: pack.nameAr || '',
        name: pack.name || '',
        description: pack.description || '',
        descriptionAr: pack.descriptionAr || '',
        price: pack.price || '',
        originalPrice: pack.originalPrice || '',
        duration: pack.duration || 30,
        niveau: pack.niveau?._id || pack.niveau || '',
        color: pack.color || '#F2C919',
        isPopular: pack.isPopular || false
      });
      setEditingId(pack._id);
    } else {
      setFormData({
        nameAr: '',
        name: '',
        description: '',
        descriptionAr: '',
        price: '',
        originalPrice: '',
        duration: 30,
        niveau: '',
        color: '#F2C919',
        isPopular: false
      });
      setEditingId(null);
    }
    setImage(null);
    onOpen();
  };

  const handleSave = async () => {
    if (!formData.nameAr || !formData.price || !formData.niveau || !formData.duration) {
      toast({ title: 'يرجى ملء الحقول الإجبارية (الاسم، السعر، المستوى، المدة)', status: 'warning', duration: 3000 });
      return;
    }

    const payload = new FormData();
    Object.keys(formData).forEach(key => {
      // If descriptionAr is not provided, fallback to description
      if (key === 'descriptionAr' && !formData[key] && formData.description) {
        payload.append(key, formData.description);
      } else {
        payload.append(key, formData[key]);
      }
    });
    if (image) {
      payload.append('image', image);
    }

    setSubmitting(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (editingId) {
        await axiosInstance.put(`/packs/${editingId}`, payload, config);
        toast({ title: 'تم التحديث بنجاح', status: 'success', duration: 3000 });
      } else {
        await axiosInstance.post('/packs', payload, config);
        toast({ title: 'تمت الإضافة بنجاح', status: 'success', duration: 3000 });
      }
      fetchData();
      onClose();
    } catch (error) {
      toast({ title: 'حدث خطأ', description: error.response?.data?.message || 'حاول مرة أخرى', status: 'error', duration: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
      try {
        await axiosInstance.delete(`/packs/${id}`);
        toast({ title: 'تم الحذف بنجاح', status: 'success', duration: 3000 });
        fetchData();
      } catch (error) {
        toast({ title: 'حدث خطأ أثناء الحذف', status: 'error', duration: 3000 });
      }
    }
  };

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box w="100%">
      <Card bg={cardBg} borderRadius="xl" mb={6}>
        <CardBody p={6}>
          <Flex justify="space-between" align="center" flexDir={{ base: 'column', md: 'row' }} gap={4}>
            <VStack align="start" spacing={2}>
              <Heading size="xl" color={secondaryColor}>إدارة الباقات (Packs)</Heading>
              <Text color="gray.600">إنشاء وتعديل باقات الاشتراكات والمستويات</Text>
            </VStack>
            <HStack spacing={4}>
              <InputGroup maxW="300px">
                <InputLeftElement><SearchIcon color="gray.600" /></InputLeftElement>
                <Input placeholder="البحث في الباقات..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </InputGroup>
              <Button bg={primaryColor} color={secondaryColor} leftIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                إضافة باقة
              </Button>
              <IconButton icon={<FiRefreshCw />} onClick={fetchData} aria-label="تحديث" />
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <Card bg={cardBg} borderRadius="xl">
          <CardBody>
            <Stat>
              <StatLabel>إجمالي الباقات</StatLabel>
              <StatNumber color={secondaryColor}>{packs.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {filteredPacks.map(pack => (
          <motion.div key={pack._id} variants={fadeIn} initial="hidden" animate="visible">
            <Card borderRadius="xl" overflow="hidden">
              <Image 
                src={pack.imageUrl ? (pack.imageUrl.startsWith('http') ? pack.imageUrl : `${axiosInstance.defaults.baseURL.replace('/api', '')}${pack.imageUrl}`) : 'https://via.placeholder.com/300x200'} 
                h="200px" 
                objectFit="cover" 
              />
              <CardBody>
                <Heading size="md" mb={2}>{pack.nameAr || pack.name}</Heading>
                <Badge colorScheme="blue" mb={3}>{pack.niveau?.name || 'غير محدد'}</Badge>
                <Text fontWeight="bold" fontSize="lg">{pack.price} {pack.currency || 'TND'}</Text>
                <HStack mt={4} justify="flex-end">
                  <IconButton icon={<EditIcon />} colorScheme="blue" variant="ghost" onClick={() => handleOpenModal(pack)} aria-label="تعديل" />
                  <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" onClick={() => handleDelete(pack._id)} aria-label="حذف" />
                </HStack>
              </CardBody>
            </Card>
          </motion.div>
        ))}
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader>{editingId ? 'تعديل الباقة' : 'إضافة باقة جديدة'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>الاسم (عربي)</FormLabel>
                <Input value={formData.nameAr} onChange={(e) => setFormData({...formData, nameAr: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>الاسم (فرنسي/انجليزي)</FormLabel>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>الوصف (فرنسي/انجليزي)</FormLabel>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>الوصف (عربي)</FormLabel>
                <Textarea value={formData.descriptionAr} onChange={(e) => setFormData({...formData, descriptionAr: e.target.value})} />
              </FormControl>
              <HStack w="100%">
                <FormControl isRequired>
                  <FormLabel>السعر</FormLabel>
                  <Input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} />
                </FormControl>
                <FormControl>
                  <FormLabel>السعر الأصلي</FormLabel>
                  <Input type="number" value={formData.originalPrice} onChange={(e) => setFormData({...formData, originalPrice: e.target.value})} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>المدة (بالأيام)</FormLabel>
                  <Input type="number" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} />
                </FormControl>
              </HStack>
              <FormControl isRequired>
                <FormLabel>المستوى (Niveau)</FormLabel>
                <Select placeholder="اختر المستوى" value={formData.niveau} onChange={(e) => setFormData({...formData, niveau: e.target.value})}>
                  {niveaux.map(n => <option key={n._id} value={n._id}>{n.name}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <Checkbox isChecked={formData.isPopular} onChange={(e) => setFormData({...formData, isPopular: e.target.checked})}>
                  علامة "الأكثر طلباً"
                </Checkbox>
              </FormControl>
              <FormControl>
                <FormLabel>صورة الباقة</FormLabel>
                <Box {...getRootProps()} border="2px dashed gray" p={6} textAlign="center" cursor="pointer" borderRadius="lg">
                  <input {...getInputProps()} />
                  <Icon as={FaUpload} boxSize={6} color="gray.400" mb={2} />
                  <Text>{image ? image.name : 'اسحب وأفلت صورة أو انقر للتحميل'}</Text>
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose} ml={3}>إلغاء</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={submitting}>حفظ</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminPacksManager;
